import { h, Fragment } from "preact";
import useFormStore from "../store/useFormStore";

export default function ServiceSelectionGrid({ services }) {
  const selected = useFormStore((state) => state.selectedServices) || [];
  const setSelected = useFormStore((state) => state.setSelectedServices);
  const clearServiceConfiguration = useFormStore(
    (state) => state.clearServiceConfiguration,
  );

  const toggleItem = (item) => {
    const currentSelected = selected || [];
    const isCurrentlySelected = currentSelected.includes(item);

    // If deselecting a service, clear its configuration from the cart
    if (isCurrentlySelected) {
      clearServiceConfiguration(item);
    }

    // Update selected services
    setSelected(
      isCurrentlySelected
        ? currentSelected.filter((i) => i !== item)
        : [...currentSelected, item],
    );
  };

  return (
    <Fragment>
      <div className="text-xl font-semibold">Choose Your Services</div>
      <p className="mb-3 md:mb-6">
        Select one or more services you're interested in:
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 md:gap-4">
        {services.map((service) => {
          const isSelected = selected.includes(service.id);

          return (
            <label
              key={service.id}
              className={`relative flex flex-col border-2 cursor-pointer transition-all hover:shadow-lg rounded-lg overflow-hidden has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-black has-[:focus-visible]:ring-offset-2 ${
                isSelected
                  ? "border-coit-success bg-green-50"
                  : "border-gray-200 hover:border-gray-300 bg-white"
              }`}
            >
              {/* Custom Checkbox - Top Right Corner */}
              <div className="absolute top-2 right-2 md:top-3 md:right-3 z-1">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleItem(service.id)}
                  className="sr-only"
                />
                <div
                  className={`w-6 h-6 md:w-7 md:h-7 rounded-full border-2 flex items-center justify-center transition-colors shadow-md ${
                    isSelected
                      ? "bg-coit-success border-coit-success"
                      : "bg-white border-gray-500"
                  }`}
                >
                  {isSelected && (
                    <svg
                      className="w-4 h-4 md:w-5 md:h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Service Image */}
              {(() => {
                const isSvg = service.image?.toLowerCase().endsWith(".svg");
                return isSvg ? (
                  <div className="w-full h-32 md:h-40 flex items-center justify-center bg-gray-50 p-3 md:p-6">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="w-full aspect-3/2 flex items-center justify-center bg-gray-50 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                );
              })()}

              {/* Service Name */}
              <div className="p-2 md:p-4 text-center leading-none">
                <span
                  className={`text-[0.7rem] md:text-base text-gray-800 ${isSelected ? "font-bold" : "font-semibold"}`}
                >
                  {service.name}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    </Fragment>
  );
}
